import { useCallback, useEffect, useState } from 'react';

export type ProgressCarouselItem = {
  src: string;
  alt?: string;
  type: 'image' | 'video';
};

type ProgressStageCarouselProps = {
  items: ProgressCarouselItem[];
  stageTitle: string;
};

function ProgressStageCarousel({ items, stageTitle }: ProgressStageCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = items.length;

  const goTo = useCallback(
    (next: number) => {
      if (total === 0) return;
      setIndex((next + total) % total);
    },
    [total]
  );

  useEffect(() => {
    setIndex(0);
  }, [items]);

  if (total === 0) {
    return null;
  }

  const current = items[index];

  return (
    <div className="progress-stage-carousel" aria-roledescription="carousel" aria-label={`${stageTitle} photos`}>
      <div className="progress-stage-carousel__viewport">
        {current.type === 'video' ? (
          <video
            key={current.src}
            className="progress-stage-carousel__media"
            src={current.src}
            controls
            playsInline
            preload="metadata"
          >
            <track kind="captions" />
          </video>
        ) : (
          <img
            key={current.src}
            className="progress-stage-carousel__media"
            src={current.src}
            alt={current.alt ?? `${stageTitle}, photo ${index + 1} of ${total}`}
            loading="lazy"
          />
        )}
      </div>

      {total > 1 && (
        <>
          <div className="progress-stage-carousel__controls">
            <button
              type="button"
              className="progress-stage-carousel__btn"
              onClick={() => goTo(index - 1)}
              aria-label="Previous image"
            >
              ‹
            </button>
            <p className="progress-stage-carousel__counter">
              {index + 1} / {total}
            </p>
            <button
              type="button"
              className="progress-stage-carousel__btn"
              onClick={() => goTo(index + 1)}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          <ul className="progress-stage-carousel__thumbs" aria-hidden={total <= 6 ? undefined : true}>
            {items.map((item, thumbIndex) => (
              <li key={item.src}>
                <button
                  type="button"
                  className={`progress-stage-carousel__thumb ${thumbIndex === index ? 'progress-stage-carousel__thumb--active' : ''}`}
                  onClick={() => setIndex(thumbIndex)}
                  aria-label={`View image ${thumbIndex + 1} of ${total}`}
                  aria-current={thumbIndex === index}
                >
                  {item.type === 'video' ? (
                    <span className="progress-stage-carousel__thumb-video">▶</span>
                  ) : (
                    <img src={item.src} alt="" loading="lazy" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default ProgressStageCarousel;
